/**
 * EventAlbumCard — Feb 2026
 *
 * Single tile in the Events grid. `featured` mode renders a wider hero
 * strip for the pinned album. Handles inline actions (pin/edit/delete/
 * download) via a hover menu when the viewer has the right permissions.
 */
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Calendar, MapPin, Camera, Film, Pin, PinOff, MoreVertical, Trash2, Pencil, Download } from "lucide-react";
import { mediaSrc } from "@/lib/authQuery";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

export default function EventAlbumCard({ album, featured, onOpen, onEdit, canModerate, onChanged }) {
  const [downloading, setDownloading] = useState(false);
  const coverUrl = album.cover ? mediaSrc(album.cover.url) : null;

  const togglePin = async () => {
    try {
      await api.post(`/event-albums/${album.id}/pin`);
      onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Pin failed")); }
  };
  const del = async () => {
    if (!window.confirm(`Delete album "${album.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/event-albums/${album.id}`);
      toast.success("Album deleted");
      onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  const downloadZip = async () => {
    setDownloading(true);
    try {
      const resp = await api.get(`/event-albums/${album.id}/download-zip`, { responseType: "arraybuffer" });
      const blob = new Blob([resp.data], { type: "application/zip" });
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = `${album.title}.zip`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "Download failed")); }
    finally { setDownloading(false); }
  };

  return (
    <article
      className={`group relative border border-border overflow-hidden cursor-pointer bg-card hover:shadow-lg transition-all ${featured ? "sm:h-64" : "aspect-[4/3]"}`}
      onClick={onOpen}
      data-testid={`event-album-card-${album.id}`}
    >
      {coverUrl ? (
        <img
          src={coverUrl}
          alt={album.title}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500 to-orange-500" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />

      {/* Badges */}
      <div className="absolute top-2 left-2 flex gap-1">
        {album.pinned && (
          <span className="text-[9px] font-bold uppercase tracking-widest bg-amber-400 text-black px-1.5 py-0.5 flex items-center gap-1" data-testid={`event-pin-badge-${album.id}`}>
            <Pin className="w-2.5 h-2.5" /> Pinned
          </span>
        )}
        {album.is_new && (
          <span className="text-[9px] font-bold uppercase tracking-widest bg-fuchsia-500 text-white px-1.5 py-0.5" data-testid={`event-new-badge-${album.id}`}>
            New
          </span>
        )}
        {album.status === "draft" && (
          <span className="text-[9px] font-bold uppercase tracking-widest bg-muted text-foreground px-1.5 py-0.5">
            Draft
          </span>
        )}
      </div>

      {/* Menu */}
      {(canModerate || onEdit) && (
        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-7 h-7 flex items-center justify-center bg-black/60 text-white hover:bg-black/80"
                data-testid={`event-menu-${album.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem onClick={onEdit} data-testid={`event-edit-${album.id}`}>
                <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={downloadZip} disabled={downloading} data-testid={`event-download-${album.id}`}>
                <Download className="w-3.5 h-3.5 mr-2" /> {downloading ? "Zipping…" : "Download ZIP"}
              </DropdownMenuItem>
              {canModerate && (
                <DropdownMenuItem onClick={togglePin} data-testid={`event-pin-${album.id}`}>
                  {album.pinned ? <><PinOff className="w-3.5 h-3.5 mr-2" /> Unpin</> : <><Pin className="w-3.5 h-3.5 mr-2" /> Pin to top</>}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={del} className="text-destructive" data-testid={`event-delete-${album.id}`}>
                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Text */}
      <div className="absolute inset-x-3 bottom-2 text-white">
        <div className="text-lg sm:text-xl font-bold leading-tight line-clamp-2" style={{ fontFamily: "var(--font-heading)" }} data-testid={`event-title-${album.id}`}>
          {album.title}
        </div>
        <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-white/85 mt-1">
          {album.event_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{album.event_date}</span>}
          {album.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{album.location}</span>}
          <span className="flex items-center gap-1"><Camera className="w-3 h-3" />{album.photo_count}</span>
          {album.video_count > 0 && <span className="flex items-center gap-1"><Film className="w-3 h-3" />{album.video_count}</span>}
        </div>
      </div>
    </article>
  );
}
